import { ClipboardList, CornerUpLeft, ShieldOff } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Badge, Button, Dialog, ErrorState, Input, plural, Select, Textarea, toast, toUiError, Tooltip } from '@orgus/ui';
import { disciplineRuleInfo, DISCIPLINE_WAIVER_MAX_DAYS } from '@orgus/protocol';
import type { Id, Violation } from '@orgus/protocol';
import { useOrgus } from '../../lib/store.ts';
import { useDisciplineActions, useMissionsPresent } from './actions.ts';
import { sendBackTargets } from './agents.ts';
import { useDisciplineState } from './useDiscipline.ts';
import { dateIn, openTasks, taskDraft, taskMarker } from './feed.ts';
import type { TaskLike } from './feed.ts';

export interface ActionTarget {
  projectId: Id;
  projectName: string;
  violations: Violation[];
}

/** Open backlog items keyed by the violation marker (empty without the missions module). */
export function useOpenTasks(): Map<string, TaskLike> {
  const items = useOrgus((s) => (s.ext.missions as { items?: TaskLike[] } | undefined)?.items);
  return useMemo(() => openTasks(items ?? []), [items]);
}

function WaiveDialog({ target, open, onOpenChange }: { target: ActionTarget; open: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useDisciplineActions();
  const first = target.violations[0]!;
  const now = Date.now();
  // The repo's own waivers.max_days, as the hub reads its rulebook.
  const maxDays = useDisciplineState()?.waiverMaxDays?.[target.projectId] ?? DISCIPLINE_WAIVER_MAX_DAYS;
  const [rule, setRule] = useState(first.rule);
  const [path, setPath] = useState(target.violations.length === 1 ? first.path : commonPath(target.violations));
  const [reason, setReason] = useState('');
  const [owner, setOwner] = useState('');
  const [expires, setExpires] = useState(dateIn(Math.min(14, maxDays), now));
  const [busy, setBusy] = useState(false);
  // A refusal from the hub, said in the dialog: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  const [tried, setTried] = useState(false);
  const errors = {
    reason: reason.trim().length < 8 ? 'Say why in a few words, so the next person knows.' : null,
    owner: owner.trim() ? null : 'Who will fix it?',
    expires: !expires || expires < dateIn(0, now) ? 'Pick a day from today on.' : expires > dateIn(maxDays, now) ? `This repo's rulebook allows at most ${maxDays} days.` : null,
    path: path.trim() ? null : 'Which file or folder (a glob works: apps/web/**).',
  };
  const ok = !errors.reason && !errors.owner && !errors.expires && !errors.path;
  const submit = async () => {
    setTried(true);
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await actions.waive({ projectId: target.projectId, rule: rule.trim(), path: path.trim(), reason: reason.trim(), owner: owner.trim(), expires });
      toast.success('Waiver granted', `${rule} on ${path} until ${expires}. It is in the audit log and the next check applies it.`);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'The waiver was not granted.', next: 'Check the fields and try again.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title="Waive a violation"
      description={`${disciplineRuleInfo(first.rule).title} in ${target.projectName}`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<ShieldOff />} onClick={() => void submit()} loading={busy}>
            Grant waiver
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input label="Rule" value={rule} onChange={(e) => setRule(e.target.value)} hint="A rule id, or a glob such as design/*" className="font-mono" />
        <Input label="Path" value={path} onChange={(e) => setPath(e.target.value)} error={tried ? errors.path : null} hint={tried && errors.path ? undefined : 'A file, or a glob such as apps/web/**'} className="font-mono" />
        <Textarea wrapperClassName="sm:col-span-2" label="Reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} error={tried ? errors.reason : null} placeholder="Why this is fine for now, and what fixes it" />
        <Input label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} error={tried ? errors.owner : null} placeholder="Who will fix it" />
        <Input label="Expires" type="date" value={expires} min={dateIn(0, now)} max={dateIn(maxDays, now)} onChange={(e) => setExpires(e.target.value)} error={tried ? errors.expires : null} hint={tried && errors.expires ? undefined : 'The last day it applies'} />
        {error != null && <ErrorState variant="inline" error={error} className="sm:col-span-2" />}
      </div>
    </Dialog>
  );
}

/** The longest folder every violation sits under, as a glob. */
export function commonPath(vs: readonly Violation[]): string {
  if (!vs.length) return '**';
  const parts = vs.map((v) => v.path.split('/'));
  const out: string[] = [];
  for (let i = 0; i < parts[0]!.length - 1; i++) {
    const seg = parts[0]![i]!;
    if (parts.every((p) => p[i] === seg && p.length > i + 1)) out.push(seg);
    else break;
  }
  return out.length ? `${out.join('/')}/**` : '**';
}

function SendBackDialog({ target, open, onOpenChange }: { target: ActionTarget; open: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useDisciplineActions();
  const sessions = useOrgus((s) => s.sessions);
  const choices = useMemo(() => sendBackTargets(sessions, target.projectId), [sessions, target.projectId]);
  const [sessionId, setSessionId] = useState(choices[0]?.id ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      await actions.sendBack({ projectId: target.projectId, sessionId, violations: target.violations, note: note.trim() });
      toast.success('Sent back to the session', 'It is told to fix them when it next tries to stop, or with the next prompt. Track it under Send-backs.');
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'Nothing was sent back.', next: 'Pick a live session Orgus launched, or open a task instead.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title="Send back to the agent"
      description={`${plural(target.violations.length, 'violation')} in ${target.projectName}`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={<CornerUpLeft />} onClick={() => void submit()} loading={busy} disabled={!sessionId}>
            Send back
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Select
          label="Session"
          value={sessionId}
          onValueChange={setSessionId}
          options={choices.map((s) => ({ value: s.id, label: `${s.title || s.id.slice(0, 8)} (${s.status === 'working' ? 'working' : s.status === 'waiting' ? 'waiting for you' : 'idle'})` }))}
          hint="Only live sessions Orgus launched can be told; watched sessions stay untouched."
        />
        <ul className="flex max-h-40 flex-col overflow-auto rounded-md border border-border-subtle bg-bg">
          {target.violations.slice(0, 20).map((v, i) => (
            <li key={`${v.path}:${v.line ?? 0}:${i}`} className="flex min-w-0 gap-3 border-b border-border-subtle px-3 py-1.5 text-sm last:border-b-0">
              <span className="shrink-0 truncate font-mono text-xs text-fg">{v.path}{v.line ? `:${v.line}` : ''}</span>
              <span className="min-w-0 truncate text-fg-muted">{v.message}</span>
            </li>
          ))}
          {target.violations.length > 20 && <li className="px-3 py-1.5 text-sm text-fg-subtle">and {target.violations.length - 20} more</li>}
        </ul>
        <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the agent should know" />
        {error != null && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}

/**
 * The three things to do with violations: send them back to a session Orgus launched,
 * waive them (reason, owner, expiry), or open a task in the backlog.
 */
export function ViolationActions({ target, compact = false }: { target: ActionTarget; compact?: boolean }) {
  const actions = useDisciplineActions();
  const missions = useMissionsPresent();
  const sessions = useOrgus((s) => s.sessions);
  const launched = useMemo(() => sendBackTargets(sessions, target.projectId).length, [sessions, target.projectId]);
  const tasks = useOpenTasks();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<'waive' | 'send' | null>(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const existing = target.violations.map((v) => tasks.get(taskMarker(target.projectId, v))).find(Boolean);

  const openTask = async () => {
    setOpening(true);
    setError(null);
    try {
      const item = await actions.openTask(taskDraft(target.projectId, target.projectName, target.violations));
      toast('Task opened', { tone: 'success', description: 'It is in the backlog on the Missions page.', action: { label: 'Open Missions', onClick: () => navigate(`/missions?item=${encodeURIComponent(item.id)}`) } });
    } catch (e) {
      setError(toUiError(e, { what: 'The task was not opened.', next: 'Try again, or copy the violations into a task by hand.' }));
    } finally {
      setOpening(false);
    }
  };

  const label = (s: string) => (compact ? <span className="sr-only sm:not-sr-only">{s}</span> : s);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Tooltip content={launched ? undefined : 'No live session Orgus launched is working in this repo. Open a task instead.'}>
        <span>
          <Button size="sm" variant="ghost" icon={<CornerUpLeft />} onClick={() => setDialog('send')} disabled={!launched}>
            {label('Send back')}
          </Button>
        </span>
      </Tooltip>
      <Button size="sm" variant="ghost" icon={<ShieldOff />} onClick={() => setDialog('waive')}>
        {label('Waive')}
      </Button>
      {existing ? (
        <Link to={`/missions?item=${encodeURIComponent(existing.id)}`} className="inline-flex">
          <Badge tone="queued" icon={<ClipboardList />}>
            Task open
          </Badge>
        </Link>
      ) : (
        <Tooltip content={missions ? undefined : 'The missions module is not on this hub, so there is no backlog to add to.'}>
          <span>
            <Button size="sm" variant="ghost" icon={<ClipboardList />} onClick={() => void openTask()} loading={opening} disabled={!missions}>
              {label('Open task')}
            </Button>
          </span>
        </Tooltip>
      )}
      {error != null && <ErrorState variant="inline" error={error} className="basis-full" />}
      {dialog === 'waive' && <WaiveDialog target={target} open onOpenChange={(o) => !o && setDialog(null)} />}
      {dialog === 'send' && <SendBackDialog target={target} open onOpenChange={(o) => !o && setDialog(null)} />}
    </div>
  );
}
